import { useState } from "react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Unlink, Loader2 } from "lucide-react";

const GoogleGlyph = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M12 11v2.8h4c-.2 1-1.3 3-4 3-2.4 0-4.3-2-4.3-4.4S9.6 8 12 8c1.4 0 2.3.6 2.8 1l1.9-1.8C15.5 6 13.9 5.3 12 5.3c-3.7 0-6.7 3-6.7 6.7s3 6.7 6.7 6.7c3.9 0 6.4-2.7 6.4-6.5 0-.4 0-.8-.1-1.2H12z" /></svg>
);

export function AccountSecurityPanel() {
  const { user, checkAuth } = useAuth();
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [gbusy, setGbusy] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw.next });
      toast.success("Password updated");
      setPw({ next: "", confirm: "" });
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setBusy(false); }
  };

  const connectGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const base = user?.role === "client" ? "/client" : "/settings";
    const redirectUrl = window.location.origin + base + "?link_google=1";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google sign-in? You can still sign in with your password.")) return;
    setGbusy(true);
    try {
      await api.post("/auth/google/unlink");
      await checkAuth();
      toast.success("Google disconnected");
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setGbusy(false); }
  };

  const inputCls = "mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black";
  const labelCls = "text-xs font-mono uppercase tracking-widest text-muted-foreground";

  return (
    <div className="space-y-8" data-testid="account-security-panel">
      {/* Change password */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4" />
          <h3 className="font-bold tracking-tight">Change password</h3>
        </div>
        {user?.has_password ? (
          <form onSubmit={changePw} className="space-y-3">
            <div>
              <label className={labelCls}>New password</label>
              <input data-testid="cp-new" type="password" required value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Confirm new password</label>
              <input data-testid="cp-confirm" type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} />
            </div>
            <button data-testid="cp-submit" disabled={busy} className="inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
            </button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">You sign in with Google, so there's no password on this account yet.</p>
        )}
      </section>

      {/* Google sign-in */}
      <section className="border-t border-border pt-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4" />
          <h3 className="font-bold tracking-tight">Google sign-in</h3>
        </div>
        {user?.google_linked ? (
          <div className="flex flex-wrap items-center gap-3">
            <span data-testid="google-connected-badge" className="inline-flex items-center gap-2 border border-emerald-600 text-emerald-700 bg-emerald-50 px-3 py-1.5 text-sm">
              <GoogleGlyph /> Connected as {user?.email}
            </span>
            <button data-testid="google-disconnect-button" onClick={disconnectGoogle} disabled={gbusy} className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm font-medium hover:border-black transition-colors disabled:opacity-50">
              {gbusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />} Disconnect
            </button>
          </div>
        ) : (
          <div>
            <p className="text-sm text-muted-foreground mb-3">Connect your Google account so you can use "Continue with Google". The Google email must match <span className="font-medium">{user?.email}</span>.</p>
            <button data-testid="google-connect-button" onClick={connectGoogle} className="inline-flex items-center gap-2 border border-black bg-white px-4 py-2.5 text-sm font-medium hover:bg-black hover:text-white transition-colors">
              <GoogleGlyph /> Connect Google
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
